import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Box,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination,
  IconButton,
  Alert
} from '@mui/material';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROLES } from '../../utils/roles';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterDialog from '../FilterDialog/FilterDialog';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { authService } from '../../services/authService';
import { formatDate } from '../../utils/dateUtils';
import { cloudinaryService } from '../../services/cloudinaryService';

function Offres() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [page, setPage] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [postulationDialog, setPostulationDialog] = useState({
    open: false,
    offreId: null,
    titre: '',
    cv: null,
    lettreMotivation: null
  });

  const [cvError, setCvError] = useState('');
  const [lettreMotivationError, setLettreMotivationError] = useState('');

  const [availableFilters, setAvailableFilters] = useState({
    titre: {
      label: 'Titre de l\'offre',
      type: 'text'
    },
    entrepriseNom: {
      label: 'Nom de l\'entreprise',
      type: 'text'
    },
    technologies: {
      label: 'Technologies',
      type: 'select',
      options: [],
      multiple: true
    },
    localisation: {
      label: 'Localisation',
      type: 'select',
      options: [],
      multiple: true
    },
    duree: {
      label: 'Durée du stage',
      type: 'select',
      options: ['2 mois', '3 mois', '4 mois', '5 mois', '6 mois']
    },
    remuneration: {
      label: 'Rémunération',
      type: 'select',
      options: ['< 600€', '600€ - 800€', '800€ - 1000€', '> 1000€']
    },
    periodeDebut: {
      label: 'Mois de début',
      type: 'select',
      options: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ],
      multiple: true
    }
  });

  const [activeFilters, setActiveFilters] = useState({
    titre: '',
    entrepriseNom: '',
    technologies: [],
    localisation: [],
    duree: [],
    remuneration: [],
    periodeDebut: []
  });

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [allCandidatures, setAllCandidatures] = useState({});

  const offresParPage = 6;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Récupérer les offres (accessible à tous)
      const offresResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
        }
      });

      if (!offresResponse.ok) {
        throw new Error('Erreur lors de la récupération des offres');
      }

      const offresData = await offresResponse.json();
      setOffres(offresData.offres || []);
      setLoading(false); // On arrête le loading principal dès que les offres sont chargées

      // Récupérer la wishlist si l'utilisateur est un étudiant
      if (userRole === ROLES.ETUDIANT) {
        try {
          const wishlistResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/my-wishlist`, {
            headers: {
              'Authorization': `Bearer ${authService.getToken()}`,
            }
          });

          if (wishlistResponse.ok) {
            const wishlistData = await wishlistResponse.json();
            setWishlist(wishlistData.wishlist || []);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données étudiant:', error);
          setWishlist([]);
        }
      }

      // Extraire et mettre à jour les filtres immédiatement
      const technologies = [...new Set(offresData.offres.flatMap(o => o.technologies.map(t => t.nom_techno)))].sort();
      const localisations = [...new Set(offresData.offres.map(o => o.localisation))].filter(Boolean).sort();
      
      setAvailableFilters(prev => ({
        ...prev,
        technologies: { 
          ...prev.technologies, 
          options: technologies,
          multiple: true 
        },
        localisation: { 
          ...prev.localisation, 
          options: localisations,
          multiple: true 
        }
      }));

      // Récupérer les statistiques en arrière-plan
      const fetchStats = async () => {
        for (const offre of offresData.offres) {
          try {
            const statsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${offre.id_offres}/stats`, {
              headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
              }
            });
            if (statsResponse.ok) {
              const statsData = await statsResponse.json();
              setAllCandidatures(prev => ({
                ...prev,
                [offre.id_offres]: statsData.nombreCandidatures
              }));
            }
          } catch (error) {
            console.error(`Erreur lors de la récupération des stats pour l'offre ${offre.id_offres}:`, error);
          }
        }
      };

      // Lancer le chargement des stats en arrière-plan
      fetchStats();

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Une erreur est survenue lors du chargement des données');
      setOffres([]);
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    setPage(pageParam);
  }, [location.search]);

  const handleToggleWishlist = async (offreId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const method = wishlist.includes(offreId) ? 'DELETE' : 'POST';
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/wishlist/${offreId}`, {
        method: method,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la modification de la wishlist');
      }

      setWishlist(prev => 
        method === 'DELETE' 
          ? prev.filter(id => id !== offreId)
          : [...prev, offreId]
      );
    } catch (err) {
      console.error("Erreur lors de la modification de la wishlist:", err);
    }
  };

  const handlePostulerClick = (offre) => {
    setPostulationDialog({
      open: true,
      offreId: offre.id,
      titre: offre.titre
    });
  };

  const handlePostuler = async () => {
    try {
      if (!postulationDialog.cv || !postulationDialog.lettreMotivation) {
        setError('Veuillez fournir un CV et une lettre de motivation');
        return;
      }

      setLoading(true);
      
      // Upload des fichiers vers Cloudinary
      const cvUrl = await cloudinaryService.uploadToCloudinary(postulationDialog.cv, 'cv');
      const lmUrl = await cloudinaryService.uploadToCloudinary(postulationDialog.lettreMotivation, 'lm');

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${postulationDialog.offreId}/postuler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cv_url: cvUrl,
          lettre_motivation_url: lmUrl
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de la candidature');
      }

      setCandidatures(prev => [...prev, postulationDialog.offreId]);
      setSuccess(true);
      
      setTimeout(() => {
        setSuccess(false);
        setPostulationDialog({
          open: false,
          offreId: null,
          titre: '',
          cv: null,
          lettreMotivation: null
        });
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la candidature:', error);
      setError('Une erreur est survenue lors de l\'envoi de la candidature');
    } finally {
      setLoading(false);
    }
  };

  const handlePostulerCancel = () => {
    setPostulationDialog({ open: false, offreId: null, titre: '' });
  };

  const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      // Vérification de la taille (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        if (type === 'cv') {
          setCvError('Le fichier est trop volumineux (max 5MB)');
        } else {
          setLettreMotivationError('Le fichier est trop volumineux (max 5MB)');
        }
        return;
      }

      // Vérification du type de fichier
      if (file.type !== 'application/pdf') {
        if (type === 'cv') {
          setCvError('Seuls les fichiers PDF sont acceptés');
        } else {
          setLettreMotivationError('Seuls les fichiers PDF sont acceptés');
        }
        return;
      }

      setPostulationDialog(prev => ({
        ...prev,
        [type]: file
      }));

      if (type === 'cv') {
        setCvError('');
      } else {
        setLettreMotivationError('');
      }
    }
  };

  const getMonthFromString = (dateStr) => {
    // Format: "D/MM/YY" (exemple: "9/03/24")
    const [day, month] = dateStr.split('/');
    return parseInt(month) - 1; // -1 car les mois commencent à 0 en JavaScript
  };

  const getMonthIndex = (monthStr) => {
    return [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ].indexOf(monthStr);
  };

  const filteredOffres = useMemo(() => {
    if (!Array.isArray(offres)) return [];
    let filtered = [...offres];
    
    // Appliquer les filtres
    if (activeFilters.titre) {
      filtered = filtered.filter(offre => 
        offre.titre.toLowerCase().includes(activeFilters.titre.toLowerCase())
      );
    }

    if (activeFilters.entrepriseNom) {
      filtered = filtered.filter(offre => 
        offre.entreprises[0]?.nom.toLowerCase().includes(activeFilters.entrepriseNom.toLowerCase())
      );
    }

    if (activeFilters.technologies.length > 0) {
      filtered = filtered.filter(offre => 
        activeFilters.technologies.every(tech => 
          offre.technologies.some(t => t.nom_techno.toLowerCase() === tech.toLowerCase())
        )
      );
    }

    if (activeFilters.localisation.length > 0) {
      filtered = filtered.filter(offre =>
        activeFilters.localisation.includes(offre.localisation)
      );
    }

    if (activeFilters.duree.length > 0) {
      filtered = filtered.filter(offre =>
        activeFilters.duree.includes(offre.duree)
      );
    }

    if (activeFilters.remuneration.length > 0) {
      filtered = filtered.filter(offre => {
        const remuneration = parseFloat(offre.remuneration);
        return activeFilters.remuneration.some(range => {
          if (range === '< 600€') return remuneration < 600;
          if (range === '600€ - 800€') return remuneration >= 600 && remuneration <= 800;
          if (range === '800€ - 1000€') return remuneration > 800 && remuneration <= 1000;
          if (range === '> 1000€') return remuneration > 1000;
          return false;
        });
      });
    }

    if (activeFilters.periodeDebut.length > 0) {
      filtered = filtered.filter(offre => {
        const offreMonth = new Date(offre.dateDebut).getMonth();
        return activeFilters.periodeDebut.some(month => 
          getMonthIndex(month) === offreMonth
        );
      });
    }

    return filtered;
  }, [offres, activeFilters]);

  // Calculer les offres à afficher pour la page courante
  const indexDernierElement = page * offresParPage;
  const indexPremierElement = indexDernierElement - offresParPage;
  const nombrePages = Math.ceil(filteredOffres.length / offresParPage);
  const offresAffichees = filteredOffres.slice(indexPremierElement, indexDernierElement);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    navigate(`/offres?page=${newPage}`);
    // Scroll vers le haut de la liste
    window.scrollTo({
      top: document.getElementById('offres-list').offsetTop - 20,
      behavior: 'smooth'
    });
  };

  const handleCardClick = (e, offreId) => {
    if (e.target.closest('.edit-button')) {
      e.stopPropagation();
      navigate(`/offres/${offreId}/edit`);
      return;
    }
    navigate(`/offres/${offreId}`, { 
      state: { 
        from: '/offres',
        page: page 
      } 
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          align="center" 
          gutterBottom
          sx={{
            opacity: 0,
            animation: 'fadeIn 0.8s ease-out forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          Offres de Stage
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 4, 
          gap: 1, 
          flexWrap: 'wrap',
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out 0.3s forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}>
          {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/offres/new')}
              sx={{
                mb: 2,
                mr: 2
              }}
            >
              Ajouter une offre
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Filtrer
          </Button>
          {Object.entries(activeFilters).map(([key, value]) => (
            (value && (Array.isArray(value) ? value.length > 0 : value !== '')) && (
              <Chip
                key={key}
                label={`${availableFilters[key].label}: ${Array.isArray(value) ? value.join(', ') : value}`}
                onDelete={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    [key]: Array.isArray(value) ? [] : ''
                  }));
                }}
                sx={{ mb: 2 }}
              />
            )
          ))}
        </Box>

        <FilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          onApply={(newFilters) => {
            setActiveFilters(newFilters);
            setFilterDialogOpen(false);
            setPage(1);
          }}
          filters={availableFilters}
          activeFilters={activeFilters}
          title="Filtrer les offres"
        />

        <Box id="offres-list">
          <Grid container spacing={3}>
            {offresAffichees.map((offre, index) => (
              <Grid 
                item 
                xs={12} 
                key={offre.id_offres}
                sx={{ 
                  opacity: 0,
                  animation: `fadeIn 0.8s ease-out ${0.2 + index * 0.1}s forwards`,
                  '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(20px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                <Card 
                  onClick={(e) => handleCardClick(e, offre.id_offres)}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {offre.titre}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          {offre.entreprises[0]?.nom}
                        </Typography>
                      </Box>
                      {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/offres/${offre.id_offres}/edit`);
                          }}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {offre.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {offre.technologies.map((tech, index) => (
                        <Chip 
                          key={index}
                          label={tech.nom_techno}
                          size="small"
                          color="primary"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="primary" />
                        <Typography variant="body2">
                          Durée : {offre.duree}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EuroIcon color="primary" />
                        <Typography variant="body2">
                          {offre.remuneration}€
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon color="primary" />
                        <Typography variant="body2">
                          Début : {formatDate(offre.dateDebut)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon color="primary" />
                        <Typography variant="body2">
                          {offre.localisation}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon color="primary" />
                        <Typography>
                          {allCandidatures[offre.id_offres] !== undefined 
                            ? `${allCandidatures[offre.id_offres]} candidature${allCandidatures[offre.id_offres] !== 1 ? 's' : ''}`
                            : <CircularProgress size={16} />
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination avec animation basée sur le temps */}
          {nombrePages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
                opacity: 0,
                animation: 'fadeIn 0.8s ease-out 1.5s forwards',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Pagination 
                count={nombrePages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                size="large"
              />
            </Box>
          )}

          {/* Message si aucune offre */}
          {filteredOffres.length === 0 && (
            <Box sx={{ 
              textAlign: 'center', 
              mt: 4,
              opacity: 0,
              animation: 'fadeIn 0.8s ease-out 0.5s forwards',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography variant="h6" color="text.secondary">
                Aucune offre ne correspond à vos critères
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            top: 20, 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '300px'
          }}
        >
          Candidature envoyée avec succès !
        </Alert>
      )}

      {/* Dialog de confirmation de postulation */}
      <Dialog
        open={postulationDialog.open}
        onClose={handlePostulerCancel}
        aria-labelledby="postulation-dialog-title"
        aria-describedby="postulation-dialog-description"
      >
        <DialogTitle id="postulation-dialog-title">
          Postuler à l'offre
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="postulation-dialog-description" sx={{ mb: 2 }}>
            Pour postuler, veuillez fournir votre CV et une lettre de motivation au format PDF.
          </DialogContentText>
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 1 }}
            >
              {postulationDialog.cv ? 'Changer le CV' : 'Ajouter votre CV'}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'cv')}
              />
            </Button>
            {postulationDialog.cv && (
              <Typography variant="caption" color="primary">
                Fichier sélectionné : {postulationDialog.cv.name}
              </Typography>
            )}
            {cvError && (
              <Typography variant="caption" color="error">
                {cvError}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 1 }}
            >
              {postulationDialog.lettreMotivation ? 'Changer la lettre' : 'Ajouter votre lettre de motivation'}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'lettreMotivation')}
              />
            </Button>
            {postulationDialog.lettreMotivation && (
              <Typography variant="caption" color="primary">
                Fichier sélectionné : {postulationDialog.lettreMotivation.name}
              </Typography>
            )}
            {lettreMotivationError && (
              <Typography variant="caption" color="error">
                {lettreMotivationError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePostulerCancel}>Annuler</Button>
          <Button 
            onClick={handlePostuler}
            variant="contained"
            disabled={!postulationDialog.cv || !postulationDialog.lettreMotivation}
          >
            Confirmer la candidature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Offres; 